import {
  useGqlMutation,
  getQueryKey,
  useOrgId,
} from "@/lib/GraphQLCodegen/fetcher";
import {
  updateImportSettingsMutation,
  importSettingRecord,
} from "@/app/Import/GraphQL/updateImportSettingsMutation";
import { RefObject, useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AgGridReact } from "ag-grid-react";
import { isEqual } from "@/lib/Utils/isEqual";

export function useUpdateImportSettings(
  supplierId: string,
  gridRef: RefObject<AgGridReact | null>,
  initialGridState: any,
) {
  const queryClient = useQueryClient();
  const orgId = useOrgId();
  const [hasChanges, setHasChanges] = useState(false);

  const { mutate, isPending } = useGqlMutation(updateImportSettingsMutation, {
    onSuccess: () => {
      setHasChanges(false);
      return queryClient.invalidateQueries({
        queryKey: getQueryKey(importSettingRecord, { supplierId }, orgId),
      });
    },
  });

  const checkForChanges = useMemo(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (gridRef.current?.api) {
          const currentState = gridRef.current.api.getState();
          const changed = !isEqual(currentState, initialGridState);
          setHasChanges(changed);
        }
      }, 300);
    };
  }, [gridRef, initialGridState]);

  const handleSave = () => {
    if (gridRef.current?.api) {
      const currentState = gridRef.current.api.getState();
      mutate({
        input: {
          supplier_id: supplierId,
          grid_state: currentState,
        },
      });
    }
  };

  const handleReset = () => {
    if (gridRef.current?.api) {
      if (initialGridState) {
        gridRef.current.api.setState(initialGridState);
      } else {
        gridRef.current.api.resetColumnState();
      }
      setHasChanges(false);
    }
  };

  return {
    isPending,
    hasChanges,
    handleSave,
    handleReset,
    onGridUpdate: checkForChanges,
  };
}
